import React, { useEffect, useState } from "react";
import { Plus, Save, Trash2, X, RefreshCcw, Home, MapPin } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { resolveImageUrl } from "../../lib/imageUrl";
import LocationPicker from "../../components/LocationPicker";
import ImageDropzone from "../../components/ImageDropzone";

const empty = () => ({
    title: "Yeni İlan",
    city: "",
    listing_type: "sale",
    price: 0,
    currency: "SAR",
    rooms: "",
    area_sqm: 0,
    description: "",
    images: [],
    features: [],
    active: true,
});

const AdminProperties = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState({});
    const [locPicker, setLocPicker] = useState({ open: false, idx: 0 });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/properties");
            setItems(data);
        } catch { toast.error("İlanlar yüklenemedi"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const update = (i, field, value) => {
        const n = [...items];
        if (["price", "area_sqm"].includes(field)) value = Number(value) || 0;
        n[i][field] = value;
        setItems(n);
    };

    const addNew = async () => {
        try {
            const { data } = await api.post("/admin/properties", empty());
            setItems([data, ...items]);
            toast.success("Yeni ilan eklendi");
        } catch { toast.error("Eklenemedi"); }
    };

    const save = async (i) => {
        const p = items[i];
        setSaving((s) => ({ ...s, [p.id]: true }));
        try {
            await api.put(`/admin/properties/${p.id}`, p);
            toast.success("Kaydedildi");
        } catch { toast.error("Kaydedilemedi"); }
        finally { setSaving((s) => ({ ...s, [p.id]: false })); }
    };

    const remove = async (i) => {
        const p = items[i];
        if (!window.confirm(`"${p.title}" silinsin mi?`)) return;
        try {
            await api.delete(`/admin/properties/${p.id}`);
            setItems(items.filter((_, idx) => idx !== i));
            toast.success("Silindi");
        } catch { toast.error("Silinemedi"); }
    };

    const removeImage = (i, gi) => {
        const n = [...items];
        n[i].images.splice(gi, 1);
        setItems(n);
    };

    const addFeature = (i, value) => {
        const v = String(value || "").trim();
        if (!v) return;
        const n = [...items];
        n[i].features = [...(n[i].features || []), v];
        setItems(n);
    };
    const removeFeature = (i, fi) => {
        const n = [...items];
        n[i].features.splice(fi, 1);
        setItems(n);
    };

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <h1 className="font-serif text-4xl mb-2">Emlak İlanları</h1>
                    <p className="text-muted_green text-sm">Satılık ve kiralık evler — sitede "Emlak" sekmesinde görünür.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} className="btn-outline-gold !text-xs" data-testid="refresh-properties"><RefreshCcw className="w-3.5 h-3.5" /> Yenile</button>
                    <button onClick={addNew} className="btn-gold !text-xs" data-testid="add-property"><Plus className="w-3.5 h-3.5" /> Yeni İlan</button>
                </div>
            </div>

            {loading ? (
                <div className="text-muted_green text-sm">Yükleniyor…</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-border_green p-12 text-center">
                    <Home className="w-12 h-12 text-muted_green mx-auto mb-3" />
                    <div className="text-muted_green text-sm">Henüz ilan yok. &ldquo;Yeni İlan&rdquo; ile başlayın.</div>
                </div>
            ) : (
                <div className="space-y-6">
                    {items.map((p, i) => (
                        <div key={p.id} className="border border-border_green bg-emerald_surface p-5" data-testid={`admin-property-${p.id}`}>
                            <div className="flex items-center justify-between mb-4 gap-2">
                                <input className="input-dark text-lg font-serif flex-1" value={p.title} onChange={(e) => update(i, "title", e.target.value)} placeholder="İlan başlığı" data-testid={`prop-title-${p.id}`} />
                                <label className="flex items-center gap-2 text-xs">
                                    <input type="checkbox" checked={!!p.active} onChange={(e) => update(i, "active", e.target.checked)} />
                                    <span className="text-muted_green">Aktif</span>
                                </label>
                                <button onClick={() => save(i)} disabled={saving[p.id]} className="btn-gold !text-xs" data-testid={`prop-save-${p.id}`}><Save className="w-3.5 h-3.5" /> Kaydet</button>
                                <button onClick={() => remove(i)} className="text-rose-400 hover:text-rose-500 p-2" data-testid={`prop-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-5">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-gold mb-1">Fotoğraflar ({(p.images || []).length})</div>
                                    {(p.images || []).length > 0 ? (
                                        <div className="grid grid-cols-3 gap-1.5 mb-2">
                                            {(p.images || []).map((g, gi) => (
                                                <div key={gi} className="relative aspect-[4/3] bg-emerald_deep border border-border_green overflow-hidden">
                                                    <img src={resolveImageUrl(g)} alt="" className="w-full h-full object-cover" />
                                                    <button onClick={() => removeImage(i, gi)} className="absolute top-0.5 right-0.5 bg-rose-600 text-white rounded-full p-0.5">
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                    {gi === 0 && <span className="absolute bottom-0.5 left-0.5 bg-emerald_deep/85 text-gold text-[9px] px-1">Kapak</span>}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="aspect-[16/10] bg-emerald_deep border border-border_green mb-2 flex items-center justify-center">
                                            <Home className="w-12 h-12 text-muted_green" />
                                        </div>
                                    )}
                                    <ImageDropzone
                                        testId={`prop-upload-${p.id}`}
                                        onUploaded={(urls) => {
                                            const n = [...items];
                                            n[i].images = [...(n[i].images || []), ...urls];
                                            setItems(n);
                                        }}
                                    />
                                </div>

                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="label-dark">Tip</label>
                                            <select className="input-dark" value={p.listing_type} onChange={(e) => update(i, "listing_type", e.target.value)} data-testid={`prop-type-${p.id}`}>
                                                <option value="sale" className="bg-emerald_surface">Satılık</option>
                                                <option value="rent" className="bg-emerald_surface">Kiralık</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="label-dark">Şehir / Bölge</label>
                                            <input className="input-dark" value={p.city} onChange={(e) => update(i, "city", e.target.value)} placeholder="Mekke / Medine / İstanbul" data-testid={`prop-city-${p.id}`} />
                                        </div>
                                        <div>
                                            <label className="label-dark">Fiyat</label>
                                            <input type="number" className="input-dark" value={p.price} onChange={(e) => update(i, "price", e.target.value)} data-testid={`prop-price-${p.id}`} />
                                        </div>
                                        <div>
                                            <label className="label-dark">Para Birimi</label>
                                            <select className="input-dark" value={p.currency} onChange={(e) => update(i, "currency", e.target.value)}>
                                                {["SAR", "USD", "TRY", "EUR"].map((c) => <option key={c} value={c} className="bg-emerald_surface">{c}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="label-dark">Oda (örn 3+1)</label>
                                            <input className="input-dark" value={p.rooms} onChange={(e) => update(i, "rooms", e.target.value)} placeholder="3+1" />
                                        </div>
                                        <div>
                                            <label className="label-dark">Alan (m²)</label>
                                            <input type="number" className="input-dark" value={p.area_sqm} onChange={(e) => update(i, "area_sqm", e.target.value)} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="label-dark">Açıklama</label>
                                        <textarea className="input-dark" rows={4} value={p.description} onChange={(e) => update(i, "description", e.target.value)} placeholder="Konum, kat, özellikler..." data-testid={`prop-desc-${p.id}`} />
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="label-dark !mb-0">Konum (Harem'e uzaklık için)</label>
                                            <button type="button" onClick={() => setLocPicker({ open: true, idx: i })} className="btn-outline-gold !text-[10px] !px-2 !py-1" data-testid={`prop-loc-picker-${p.id}`}>
                                                <MapPin className="w-3 h-3" /> Haritadan Seç
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <input type="number" step="any" className="input-dark" placeholder="Enlem (21.4225)" value={p.latitude ?? ""} onChange={(e) => update(i, "latitude", e.target.value === "" ? null : Number(e.target.value))} data-testid={`prop-lat-${p.id}`} />
                                            <input type="number" step="any" className="input-dark" placeholder="Boylam (39.8262)" value={p.longitude ?? ""} onChange={(e) => update(i, "longitude", e.target.value === "" ? null : Number(e.target.value))} data-testid={`prop-lng-${p.id}`} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="label-dark">Özellikler</label>
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {(p.features || []).map((f, fi) => (
                                                <span key={fi} className="bg-emerald_deep/60 border border-border_green text-bone text-xs px-2 py-1 inline-flex items-center gap-1">
                                                    {f}
                                                    <button onClick={() => removeFeature(i, fi)} className="text-rose-400 hover:text-rose-500"><X className="w-3 h-3" /></button>
                                                </span>
                                            ))}
                                        </div>
                                        <input
                                            className="input-dark"
                                            placeholder="Özellik ekle ve Enter (örn: Harem'e 500m, Asansör, Eşyalı)"
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(i, e.target.value); e.target.value = ""; } }}
                                            data-testid={`prop-add-feature-${p.id}`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <LocationPicker
                open={locPicker.open}
                city={/medine|medina/i.test(items[locPicker.idx]?.city || "") ? "Medina" : "Mecca"}
                initialLat={items[locPicker.idx]?.latitude ?? undefined}
                initialLng={items[locPicker.idx]?.longitude ?? undefined}
                onClose={() => setLocPicker({ open: false, idx: 0 })}
                onSelect={(la, ln) => {
                    const n = [...items];
                    if (n[locPicker.idx]) { n[locPicker.idx].latitude = la; n[locPicker.idx].longitude = ln; }
                    setItems(n);
                    toast.success("Konum seçildi — kaydet'i unutmayın");
                }}
            />
        </div>
    );
};

export default AdminProperties;
